import plansConfig from '@/configs/billing/plans.json';
import type { BillingPlansConfig, PlanConfig, AddonConfig, ModeConfig } from './types';
import type { BillingPlanCode, BillingFeatureKey } from '@/lib/billing/plans';
import type { EntitlementFeatureKey } from '@/lib/billing/resolve-entitlement';
import type { BillingLifecycleStatus } from '@/lib/billing/entitlements';
import type { PosFeatureKey } from './types';

const config = plansConfig as unknown as BillingPlansConfig;

export function getPlan(planCode: string): PlanConfig | undefined {
  return config.plans[planCode];
}

export function getPlanFeatures(planCode: string): BillingFeatureKey[] {
  const plan = getPlan(planCode);
  return plan ? [...plan.coreFeatures] : [];
}

export function getPlanPosFeatures(planCode: string): PosFeatureKey[] {
  const plan = getPlan(planCode);
  return plan ? [...plan.posFeatures] : [];
}

/**
 * Union of all core feature keys declared anywhere in config: every plan's
 * `coreFeatures` plus every pack's `features`.
 *
 * Packs are included because a core feature may be granted ONLY by a pack
 * (e.g. `bar_tabs` ships on `bar_pack`, not on any plan tier). Deriving the
 * universe from plans alone would silently drop those keys, so the entitlement
 * axis in `resolve-entitlement.ts` would no longer span every valid key.
 */
export function getAllCoreFeatureKeys(): readonly string[] {
  const keys = new Set<string>();
  for (const plan of Object.values(config.plans)) {
    for (const f of plan.coreFeatures) keys.add(f);
  }
  for (const pack of Object.values(config.packs ?? {})) {
    for (const f of pack.features ?? []) keys.add(f);
  }
  return [...keys].sort();
}

/** Union of all POS feature keys across every plan and addon in config. */
export function getAllPosFeatureKeys(): readonly string[] {
  const keys = new Set<string>();
  for (const plan of Object.values(config.plans)) {
    for (const f of plan.posFeatures) keys.add(f);
  }
  for (const addon of Object.values(config.addons)) {
    for (const f of addon.features) keys.add(f);
  }
  return [...keys].sort();
}

export function getPlanLimits(planCode: string) {
  const plan = getPlan(planCode);
  return plan?.limits ?? { branches: 0, menus: 1, menuItems: 30, apiKeys: 0, teamMembers: 0 };
}

/**
 * The platform split percentage (Menuraq's share of each online transaction)
 * for a given plan. Falls back to 0% for unknown or legacy plan codes.
 */
export function getPlanPlatformFeePercent(planCode: string): number {
  return getPlan(planCode)?.platformFeePercent ?? 0;
}

export function hasFeature(planCode: string, feature: BillingFeatureKey): boolean {
  return getPlanFeatures(planCode).includes(feature);
}

export function hasPosFeature(planCode: string, feature: PosFeatureKey): boolean {
  return getPlanPosFeatures(planCode).includes(feature);
}

export function getIncludedPosAddon(planCode: string): string | null {
  const plan = getPlan(planCode);
  return plan?.includedPosAddon ?? null;
}

export function getAddon(addonCode: string): AddonConfig | undefined {
  return config.addons[addonCode];
}

export function getAddonFeatureFlags(addonCode: string): string[] {
  const addon = getAddon(addonCode);
  return addon?.featureFlags ?? [];
}

export function getAddonFeatures(addonCode: string): PosFeatureKey[] {
  const addon = getAddon(addonCode);
  return addon ? [...addon.features] : [];
}

export function getMode(modeCode: string): ModeConfig | undefined {
  return config.modes[modeCode];
}

export function isLifecycleActive(status: BillingLifecycleStatus): boolean {
  return config.lifecycleStatuses.active.includes(status);
}

export function getVolumeDiscount(branchCount: number): number {
  let discount = 0;
  for (const tier of config.volumeDiscounts) {
    if (branchCount >= tier.minBranches) {
      discount = tier.discount;
    }
  }
  return discount;
}

export function getUpgradePath(planCode: string): string | null {
  const plan = getPlan(planCode);
  return plan?.upgradePath ?? null;
}

export function getAllPlanCodes(): string[] {
  return config.planCodes;
}

export const PLAN_CODES_TUPLE = config.planCodes as [string, ...string[]];

export const PAID_PLAN_CODES_TUPLE = config.planCodes.filter(c => c !== 'free') as [string, ...string[]];

export function getAllAddonCodes(): string[] {
  return config.addonCodes;
}

export function getAllModeCodes(): string[] {
  return config.modeCodes;
}

/** All 9 operating mode keys (including structural modes not in modeCodes). */
export const ALL_OPERATING_MODE_KEYS = [
  'dine_in', 'counter_pickup', 'pickup',
  'bar_service', 'weight_pricing', 'room_folio',
  'delivery_managed', 'scheduled_pre_order', 'ghost_kitchen',
] as const;

export type OperatingModeDisplay = {
  key: string;
  label: string;
  description: string;
  customerLabel: string;
  customerDescription: string;
  adminLabel: string;
  adminDescription: string;
  billingFeature: BillingFeatureKey | null;
  minimumPlan: string | null;
  monthly: number;
  yearly: number;
};

export function getModeDisplay(modeKey: string): OperatingModeDisplay | undefined {
  const mode = config.modes[modeKey];
  if (!mode) return undefined;
  return { key: modeKey, ...mode };
}

export function getAllModeDisplays(): OperatingModeDisplay[] {
  return ALL_OPERATING_MODE_KEYS
    .map(getModeDisplay)
    .filter((m): m is OperatingModeDisplay => m !== undefined);
}

// POS feature flag display metadata
export interface PosFeatureFlagDisplay {
  key: string;
  label: string;
  description: string;
}

export function getPosFeatureFlagDisplay(flagKey: string): PosFeatureFlagDisplay | undefined {
  const flags = (config as unknown as { posFeatureFlags?: Record<string, { label: string; description: string }> }).posFeatureFlags;
  if (!flags?.[flagKey]) return undefined;
  return { key: flagKey, ...flags[flagKey] };
}

export function getAllPosFeatureFlagDisplays(): PosFeatureFlagDisplay[] {
  const flags = (config as unknown as { posFeatureFlags?: Record<string, { label: string; description: string }> }).posFeatureFlags;
  if (!flags) return [];
  return Object.entries(flags).map(([key, v]) => ({ key, ...v }));
}

export function getAllFeatures(): BillingFeatureKey[] {
  const all = new Set<BillingFeatureKey>();
  for (const planCode of config.planCodes) {
    for (const f of getPlanFeatures(planCode)) {
      all.add(f);
    }
  }
  return [...all];
}

export function resolveEntitlement(
  planCode: string,
  posAddonCode: string | null,
  modes: string[]
): Set<EntitlementFeatureKey> {
  const features = new Set<EntitlementFeatureKey>();
  const plan = getPlan(planCode);
  if (!plan) return features;

  for (const f of plan.coreFeatures) features.add(f as EntitlementFeatureKey);
  for (const f of plan.posFeatures) features.add(f as EntitlementFeatureKey);

  if (posAddonCode) {
    const addon = getAddon(posAddonCode);
    if (addon) {
      for (const f of addon.features) features.add(f as EntitlementFeatureKey);
    }
  }

  for (const mode of modes) {
    const modeConfig = getMode(mode);
    if (modeConfig?.billingFeature) {
      features.add(modeConfig.billingFeature as EntitlementFeatureKey);
    }
  }

  return features;
}

export { config as billingConfig };
